import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { serverKeys, siteKeys } from '../../core/query-keys';
import { serverEndpoints } from './servers.endpoints';
import type { ClaimServerInput, ServerListResponse } from './servers.types';

/**
 * A server/site mutation changes state read in three places: the servers list, the sites list, and
 * the fleet overview (which drives the top site-switcher's status dot). They all derive from the
 * same backend rows, so a single mutation must refresh ALL of them — otherwise the switcher shows a
 * stale status while the list is current (R-152 fix: keep the caches coherent = one reactive SSOT).
 */
export function invalidateFleet(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: serverKeys.all });
  qc.invalidateQueries({ queryKey: siteKeys.all });
  qc.invalidateQueries({ queryKey: ['alerting'] }); // fleet overview → switcher status
}

/** Announced servers (cloud + admin). Polls so the online dots stay fresh. */
export function useServers(enabled = true) {
  return useQuery({
    queryKey: serverKeys.list,
    queryFn: () => api.get<ServerListResponse>(serverEndpoints.list),
    refetchInterval: 15_000,
    enabled,
  });
}

/** Claim an unclaimed server → creates + links a site. */
export function useClaimServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instance_id, ...body }: ClaimServerInput) =>
      api.post(serverEndpoints.claim(instance_id), body),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useSetServerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ instance_id, status }: { instance_id: string; status: string }) =>
      api.post(serverEndpoints.status(instance_id), { status }),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useRefreshServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instance_id: string) => api.post(serverEndpoints.refresh(instance_id)),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useDeleteServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instance_id: string) => api.delete(serverEndpoints.remove(instance_id)),
    onSuccess: () => invalidateFleet(qc),
  });
}
